import $ from 'jquery';
import config from 'lib/config';
import * as Utils from 'lib/utils';

const W = window;
const $D = $(document);

/* prettier-ignore */
const WebsocketChannelType = {
  Discrete: 'discrete',     // Each message is an independent event. No prior context is required to interpret a message (default)
  Cumulative: 'cumulative', // Each message extends prior history and messages must consumed in order to reconstruct state/history
  Snapshot: 'snapshot',     // Each message is a complete current state. Only the latest matters; older ones can be dropped
};

const WebsocketMessageType = {
  InitAck: 'init_ack',
  SubscribeAck: 'subscribe_ack',
  UnsubscribeAck: 'unsubscribe_ack',
  Heartbeat: 'heartbeat',
  Publish: 'publish',
  Reload: 'reload',
};

class WebsocketManager {
  constructor() {
    this.uri = config.api.uri.replace(/^http/, 'ws') + config.api.subscriptions.endpoint;
    this.protocol = config.api.subscriptions.protocol;
    this.retries = 0;
    this.retry_delay = 0;
    this.state = 'play';

    this.keepalive = 0;
    this.keepalive_timeout = config.api.subscriptions.client.keepalive_timeout / 1000;
    this.keepalive_timer = null;

    this.subscriptions = {
      current: new Map(),
      queue: new Map(),
    };
  }

  pause() {
    this.state = 'pause';
    $D.trigger('websocket:state', [this.isConnected(), this.state]);
  }

  unpause() {
    this.state = 'play';
    $D.trigger('websocket:state', [this.isConnected(), this.state]);
  }

  /* prettier-ignore */
  subscribe(channels, handler) {
    //console.log('subscribe', channels);
    const connected = this.socket && this.socket.ready;
    const subs = this.subscriptions[connected ? 'current' : 'queue'];
    const payload = {
      type: 'subscribe',
      channels: [],
    };

    if (!channels) throw new Error(`Missing "channels" in websocket subscribe`);
    if (!Utils.isString(channels) && !Utils.isObject(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket subscribe`);

    if (!handler) throw new Error(`Missing "handler" in websocket subscribe`);

    for (let channel of Utils.toArray(channels)) {
      let type = WebsocketChannelType.Discrete;
      let name;

      if (Utils.isObject(channel)) {
        if (channel.type && (!Utils.isString(channel.type) || !Object.values(WebsocketChannelType).includes(channel.type))) throw new Error(`Invalid "type" of channel in websocket subscribe`);
        if (!Utils.isString(channel.name)) throw new Error(`Invalid "name" of channel in websocket subscribe`);

        if (channel.type) type = channel.type;
        name = channel.name;
      } 
      
      else {
        if (!Utils.isString(channel)) throw new Error(`Invalid "channels" in websocket subscribe`);
        name = channel;
      }

      let sub = subs.get(name);

      if (!sub) {
        sub = { ack: false, type, name, handlers: [] };
        subs.set(name, sub);
      }

      sub.handlers.push(handler);
      payload.channels.push(Utils.pick(sub, 'type', 'name'));
    }

    if (connected && payload.channels.length) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  /* prettier-ignore */
  unsubscribe(channels) {
    //console.log('unsubscribe', channels);
    const connected = this.socket && this.socket.ready;
    const subs = this.subscriptions[connected ? 'current' : 'queue'];
    const payload = {
      type: 'unsubscribe',
      channels: [],
    };

    if (!channels) throw new Error(`Missing "channels" in websocket unsubscribe`);
    if (!Utils.isString(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket unsubscribe`);

    for (const name of Utils.toArray(channels)) {
      if (!Utils.isString(name)) throw new Error(`Invalid "channels" in websocket unsubscribe`);

      if (!connected) subs.delete(name);
      payload.channels.push(name);
    }

    if (connected && payload.channels.length) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  republish(channels) {
    //console.log('republish', channels);
    const connected = this.socket && this.socket.ready;
    const payload = {
      type: 'republish',
      channels: [],
    };

    if (!channels) throw new Error(`Missing "channels" in websocket republish`);
    if (!Utils.isObject(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket republish`);

    for (const channel of Utils.toArray(channels)) {
      const { message_id, name, inclusive } = channel;

      if (!Utils.isString(message_id)) throw new Error(`Invalid "message_id" of channel in websocket republish`);
      if (!Utils.isString(name)) throw new Error(`Invalid "name" of channel in websocket republish`);

      payload.channels.push({ message_id, name, inclusive });
    }

    if (connected && payload.channels.length) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  connect() {
    console.groupCollapsed(`🕸 WS:${this.retries > 0 ? 'RE' : ''}CONNECTING${this.retries > 0 ? ` [ATTEMPT ${this.retries}]` : ''}`);
    console.log('URL', this.uri);
    console.log('PROTOCOL', this.protocol);
    console.groupEnd();

    this.socket = new WebSocket(this.uri, [this.protocol]);
    this.socket.onmessage = this.onMessage.bind(this);
    this.socket.onopen = this.onOpen.bind(this);
    this.socket.onclose = this.onClose.bind(this);
  }

  reconnect(delay) {
    if (this.socket && this.socket.ready) {
      this.retry_delay = delay || 0;
      this.socket.close(1000);
    }
  }

  onOpen() {
    this.keepalive = 0;
    this.retries = 0;
    this.retry_delay = 0;

    this.socket.send(
      JSON.stringify({
        type: 'init',
        params: {
          'X-CLIENT-HOST': W.API.host,
          'X-CLIENT-TOKEN': W.API.client,
          'X-USER-TOKEN': W.API.user,
        },
      }),
    );
  }

  onClose(evt) {
    if (evt.code !== 1000) {
      console.groupCollapsed('🕸 WS:ERROR');
      console.log('CODE', evt.code);
      console.log('REASON', evt.reason);
      console.groupEnd();
    }

    if (this.retries === 0) {
      console.log(`🕸 WS:DISCONNECTED`);
      $D.trigger('websocket:state', [false, this.state]);
    }

    if (this.subscriptions.current.size) {
      this.subscriptions.queue = this.subscriptions.current;
      this.subscriptions.current = new Map();
    }

    clearTimeout(this.keepalive_timer);
    this.socket = null;
    $D.trigger('websocket:closed');

    setTimeout(
      () => {
        this.retries++;
        this.connect();
      },
      this.retry_delay === 0
        ? config.api.subscriptions.client.retry_timeout + config.api.subscriptions.client.retry_timeout * (Math.random() * 2 - 1) * config.api.subscriptions.client.retry_random
        : this.retry_delay,
    );
  }

  onKeepAlive() {
    clearTimeout(this.keepalive_timer);

    if (this.socket && this.socket.ready) {
      this.keepalive++;

      if (this.keepalive > this.keepalive_timeout) {
        this.socket.close(1000);
        return;
      }
    }

    this.keepalive_timer = setTimeout(this.onKeepAlive.bind(this), 1000);
  }

  onMessage(evt) {
    const data = Utils.fromJSON(evt.data);

    if (data) {
      const { type } = data;

      if (!type) throw new Error(`Missing "type" in websocket message`);
      if (!Object.values(WebsocketMessageType).includes(type)) throw new Error(`Invalid "type" (${type}) in websocket message`);

      //console.log(data);

      switch (type) {
        case WebsocketMessageType.InitAck:
          this.onInitAck(data);
          $D.trigger('websocket:opened');
          break;

        case WebsocketMessageType.SubscribeAck:
          this.onSubscribeAck(data);
          break;

        case WebsocketMessageType.UnsubscribeAck:
          this.onUnsubscribeAck(data);
          break;

        case WebsocketMessageType.Heartbeat:
          this.onHeartbeat(data);
          break;

        case WebsocketMessageType.Publish:
          this.onPublish(data);
          break;

        case WebsocketMessageType.Reload:
          location.reload();
          break;
      }
    }
  }

  /* prettier-ignore */
  onInitAck(data) {
    console.groupCollapsed(`🕸 WS:CONNECTED`);
    console.log('DATA', data);
    console.groupEnd();

    this.socket.ready = true;
    this.keepalive_timer = setTimeout(this.onKeepAlive.bind(this), 1000);

    if (this.subscriptions.queue.size) {
      const subs = Array.from(this.subscriptions.queue.values());

      this.socket.send(
        JSON.stringify({
          type: 'subscribe',
          channels: subs.map(sub => Utils.pick(sub, 'type', 'name')),
        }),
      );

      this.subscriptions.current = this.subscriptions.queue;
      this.subscriptions.queue = new Map();
    }

    $D.trigger('websocket:state', [true, this.state]);
  }

  /* prettier-ignore */
  onSubscribeAck(data) {
    //console.log('onSubscribeAck', data);
    const { channels } = data;
    const missing = [];

    if (!channels) throw new Error(`Missing "channels" in websocket subscribe ack`);
    if (!Utils.isObject(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket subscribe ack`);

    for (const { message_id, name } of Utils.toArray(channels)) {
      if (message_id && !Utils.isString(message_id)) throw new Error(`Invalid "message_id" of channel in websocket subscribe ack`);
      if (!Utils.isString(name)) throw new Error(`Invalid "name" of channel in websocket subscribe ack`);

      const sub = this.subscriptions.current.get(name);

      if (sub) {
        if (sub.message_id && message_id && sub.message_id !== message_id) missing.push(Utils.pick(sub, 'message_id', 'name'));
        else sub.message_id = message_id;

        sub.ack = true;
      }
    }

    if (missing.length) this.republish(missing);
  }

  /* prettier-ignore */
  onUnsubscribeAck(data) {
    //console.log('onUnsubscribeAck', data);
    const { channels } = data;

    if (!channels) throw new Error(`Missing "channel" in websocket unsubscribe ack`);
    if (!Utils.isString(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket unsubscribe ack`);

    for (const name of Utils.toArray(channels)) {
      if (!Utils.isString(name)) throw new Error(`Invalid "channels" in websocket unsubscribe ack`);
      this.subscriptions.current.delete(name);
    }
  }

  onHeartbeat(data) {
    //console.log('onHeartbeat', data);
    const { now, channels } = data;
    const missing = [];

    if (!now) throw new Error(`Missing "now" in websocket heartbeat`);

    this.keepalive = 0;
    $D.trigger('websocket:heartbeat', [now]);

    if (this.state === 'pause') return;

    if (channels) {
      if (!Utils.isObject(channels) && !Utils.isArray(channels)) throw new Error(`Invalid "channels" in websocket heartbeat`);

      for (const { message_id, name } of Utils.toArray(channels)) {
        if (!Utils.isString(message_id)) throw new Error(`Invalid "message_id" of channel in websocket sync`);
        if (!Utils.isString(name)) throw new Error(`Invalid "name" of channel in websocket sync`);

        const sub = this.subscriptions.current.get(name);

        if (sub) {
          if (!sub.message_id) missing.push({ name: sub.name, message_id, inclusive: true });
          else if (sub.message_id !== message_id) missing.push(Utils.pick(sub, 'message_id', 'name'));
        }
      }

      if (missing.length) this.republish(missing);
    }
  }

  onPublish(data) {
    //console.log('onPublish', data);
    const { prev_message_id, message_id, channel, payload } = data;

    if (this.state === 'pause') return;
    if (!channel) throw new Error(`Missing "channel" in websocket publish`);
    if (!Utils.isString(channel)) throw new Error(`Invalid "channel" in websocket publish`);

    const sub = this.subscriptions.current.get(channel);

    if (sub) {
      if (sub.message_id && prev_message_id && sub.message_id !== prev_message_id) {
        this.republish(Utils.pick(sub, 'message_id', 'name'));
        return;
      }

      for (const handler of sub.handlers) {
        handler.call(handler, channel, payload, { message_id });
      }

      sub.message_id = message_id;
    }
  }

  isConnected() {
    return this.socket && this.socket.ready ? true : false;
  }

  isSubscribed(channel) {
    const connected = this.socket && this.socket.ready;
    const subs = this.subscriptions[connected ? 'current' : 'queue'];

    return subs.has(channel);
  }
}

const manager = new WebsocketManager();

export default manager;

$.subscribe = (channels, handler) => {
  const self = $;

  manager.subscribe(channels, (name, data, opts) => {
    if (handler) handler.call(self, name, data, opts);
    $D.trigger('websocket:messages', [name, data, opts]);
  });

  return self;
};

$.fn.subscribe = function (channels, handler) {
  const self = this;
  const $obj = $(self);

  manager.subscribe(channels, (name, data, opts) => {
    if (handler) handler.call(self, name, data, opts);
    $obj.triggerHandler(`${name}:message`, [data, opts]);
    $obj.trigger('websocket:messages', [name, data, opts]);
  });

  return self;
};

$.unsubscribe = function (channels) {
  manager.unsubscribe(channels);
  return $;
};

$.fn.unsubscribe = function (channels) {
  manager.unsubscribe(channels);
  return this;
};

$.reconnect = function (delay) {
  manager.reconnect(delay);
  return $;
};

if (config.dev) {
  W.WS = {
    connected: () => manager.isConnected(),
    subscribed: name => manager.isSubscribed(name),
    subscriptions: () => Array.from(manager.subscriptions.current.values()),
    state: () => manager.state,
    pause: () => manager.pause(),
    unpause: () => manager.unpause(),

    reconnect(delay) {
      manager.reconnect(delay);
    },

    unsubscribe(name) {
      manager.unsubscribe(name);
    },
  };
}

$.onAuth(() => {
  if (W.API.client) manager.connect();
});
